import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  InputAdornment,
  Divider
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import projectService, { Project, Order as OrderInterface } from '../../services/projectService'; // Poprawiony import projectService
import contractorService, { Contractor } from '../../services/contractorService';

interface AddOrderFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (newOrder: Omit<OrderInterface, 'id' | 'createdAt' | 'updatedAt' | 'order_id_display' | 'sequence_number' | 'gmail_draft_id' | 'gmail_message_id' | 'user' | 'project'>) => void; // Użycie Omit i OrderInterface
  onEdit: (editedOrder: OrderInterface) => void; // Użycie OrderInterface
  onDelete: (orderId: number) => void; // ID jest typu number
  editMode: boolean;
  orderData: any;
  currentProjectId?: string; // Dodane pole dla bieżącego projektu
}

const AddOrderForm: React.FC<AddOrderFormProps> = ({
  open,
  onClose,
  onAdd,
  onEdit,
  onDelete,
  editMode,
  orderData,
  currentProjectId,
}) => {
  const [contractor, setContractor] = useState('');
  const [order_content, setOrderContent] = useState(''); 
  // Usunięto stan status i allStatuses, ponieważ status będzie ustawiany automatycznie na backendzie
  const [delivery_date, setDeliveryDate] = useState<string | null>(null); 
  const [contact_person_name, setContactPersonName] = useState(''); 
  const [contact_person_surname, setContactPersonSurname] = useState(''); 
  const [contact_person_phone, setContactPersonPhone] = useState(''); 
  const [project_id, setProjectId] = useState(''); 
  const [projects, setProjects] = useState<Project[]>([]); 
  const [loadingProjects, setLoadingProjects] = useState(false); 

  // Nowe stany dla wyszukiwania kontrahentów
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Funkcja do wyszukiwania kontrahentów
  const searchContractors = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setLoadingContractors(true);
      const filters = { searchText: query };
      const response = await contractorService.filterContractors(filters, 1, 10);
      setSearchResults(response.contractors);
      setShowResults(true);
    } catch (error) {
      console.error('Błąd podczas wyszukiwania kontrahentów:', error);
      setSearchResults([]);
    } finally {
      setLoadingContractors(false);
    }
  };

  // Obsługa wyboru kontrahenta z wyników wyszukiwania
  const handleSelectContractor = (contractor: Contractor) => {
    setContractor(contractor.email || contractor.name);
    setShowResults(false);
    setSearchQuery('');
  };

  // Pobieranie projektów w realizacji
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const allProjects = await projectService.getAllProjects(); // Użycie projectService
        const activeProjects = allProjects.filter(
          project => project.project_phase === 'W REALIZACJI'
        );
        setProjects(activeProjects);
      } catch (error) {
        console.error('Błąd podczas pobierania projektów:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    if (open) {
      fetchProjects();
    }
  }, [open]);

  // Ustawianie danych formularza
  useEffect(() => {
    if (orderData) {
      // Ustawiamy dane niezależnie od trybu edycji, jeśli orderData zawiera dane
      setContractor(orderData.contractor || '');
      setOrderContent(orderData.order_content || orderData.content || ''); 
      // Usunięto ustawianie statusu, będzie domyślny
      setDeliveryDate(orderData.delivery_date || null);
      setContactPersonName(orderData.contact_person_name || '');
      setContactPersonSurname(orderData.contact_person_surname || '');
      setContactPersonPhone(orderData.contact_person_phone || '');
      setProjectId(orderData.project_id || currentProjectId || '');
    } else {
      // Resetowanie pól dla nowego zamówienia
      setContractor('');
      setOrderContent('');
      // Usunięto resetowanie statusu
      setDeliveryDate(null);
      setContactPersonName('');
      setContactPersonSurname('');
      setContactPersonPhone('');
      setProjectId(currentProjectId || '');
    }
  }, [orderData, open, currentProjectId]);

  const handleSubmit = () => {
    // const currentDate = new Date().toISOString().split('T')[0]; // Już niepotrzebne, backend używa createdAt
    
    const newOrderData = {
      ...(editMode && orderData && { id: orderData.id }), // Dodaj id tylko w trybie edycji i jeśli orderData istnieje
      contractor,
      order_content, 
      delivery_date: delivery_date || null, 
      contact_person_name,
      contact_person_surname,
      contact_person_phone,
      // Usunięto status z danych wysyłanych - backend ustawi domyślny 'Przygotowane'
      project_id, 
    };
    
    if (editMode) {
      onEdit(newOrderData as OrderInterface); // Rzutowanie na OrderInterface
    } else {
      onAdd(newOrderData);
    }
    
    onClose();
  };

  const handleDelete = () => {
    if (editMode && orderData && orderData.id) { // Sprawdzamy czy orderData.id istnieje
      onDelete(orderData.id);
      onClose();
    }
  };

  const isFormValid = () => {
    return contractor.trim() !== '' && order_content.trim() !== '' && project_id.trim() !== ''; // Dodano walidację project_id
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editMode ? 'Edytuj zamówienie' : 'Dodaj nowe zamówienie'}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="project-select-label">Projekt</InputLabel>
            <Select
              labelId="project-select-label"
              id="project-select"
              value={project_id}
              label="Projekt"
              onChange={(e) => setProjectId(e.target.value)}
              disabled={loadingProjects}
            >
              {loadingProjects ? (
                <MenuItem value="">
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                projects.map((project) => (
                  <MenuItem key={project.project_id} value={project.project_id}>
                    {project.project_id} {project.project_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Pole wyszukiwania kontrahenta */}
          <Box sx={{ mb: 2, position: 'relative' }}>
            <TextField
              fullWidth
              label="Wyszukaj kontrahenta"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchContractors(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {loadingContractors ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Search />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Wyniki wyszukiwania */}
            {showResults && searchResults.length > 0 && (
              <Paper 
                elevation={3} 
                sx={{ 
                  position: 'absolute', 
                  width: '100%', 
                  maxHeight: '200px', 
                  overflow: 'auto',
                  zIndex: 1000,
                  mt: 0.5
                }}
              >
                <List dense>
                  {searchResults.map((contractor) => (
                    <React.Fragment key={contractor.id}>
                      <ListItem 
                        button 
                        onClick={() => handleSelectContractor(contractor)}
                      >
                        <ListItemText 
                          primary={contractor.name} 
                          secondary={
                            <>
                              <Typography variant="body2" component="span">
                                {contractor.email}
                              </Typography>
                              <br />
                              <Typography variant="body2" component="span" color="text.secondary">
                                {contractor.location}, NIP: {contractor.nip}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          <TextField
            fullWidth
            label="E-mail kontrahenta" 
            variant="outlined"
            value={contractor}
            onChange={(e) => setContractor(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Treść zamówienia"
            variant="outlined"
            value={order_content} // Używamy nowej nazwy stanu
            onChange={(e) => setOrderContent(e.target.value)} // Używamy nowej funkcji ustawiającej stan
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Data dostawy"
            type="date"
            variant="outlined"
            value={delivery_date || ''} // Ustawiamy pusty string, jeśli null
            onChange={(e) => setDeliveryDate(e.target.value || null)} // Ustawiamy null, jeśli puste
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Imię osoby do kontaktu"
            variant="outlined"
            value={contact_person_name}
            onChange={(e) => setContactPersonName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Nazwisko osoby do kontaktu"
            variant="outlined"
            value={contact_person_surname}
            onChange={(e) => setContactPersonSurname(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Telefon osoby do kontaktu"
            variant="outlined"
            value={contact_person_phone}
            onChange={(e) => setContactPersonPhone(e.target.value)}
            sx={{ mb: 2 }}
          />
          {/* Usunięto zduplikowany FormControl dla projektu oraz FormControl dla statusu */}
        </Box>
      </DialogContent>
      <DialogActions>
        {editMode && (
          <Button 
            onClick={handleDelete} 
            color="error"
            variant="outlined"
            sx={{ mr: 'auto' }}
          >
            Usuń
          </Button>
        )}
        <Button onClick={onClose} color="primary" variant="outlined">
          Anuluj
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={!isFormValid()}
        >
          {editMode ? 'Zapisz zmiany' : 'Dodaj zamówienie'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrderForm;
